import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  Clock,
  ArrowRight,
  Target,
  Trophy,
  Star,
  Flame,
  Lock,
  TrendingUp,
  Map,
  Users,
  Building2,
} from "lucide-react";
import { getTempleIcon, getAchievementIcon } from "@/utils/temple-icons";
import { cn } from "@/utils/utils";
import { FirstStepsChecklist } from "@/ui/components/dashboard/FirstStepsChecklist";

import { Temple as TempleBase } from "@/data/gamification";

interface Temple extends TempleBase {
  progress_percentage?: number;
  status?: "locked" | "unlocked" | "in_progress" | "completed";
  is_featured?: boolean;
}

interface ContentProps {
  user: {
    full_name: string;
    xp: number;
    level: number;
    total_progress_percentage: number;
  };
  temples: Temple[];
  notifications: {
    recent_reminders: Array<{ title: string; message: string }>;
  };
  epicGoal: {
    title: string;
    deadline_weeks: number;
    progress_percentage: number;
    status: string;
  };
  achievements: Array<{
    name: string;
    description: string;
    icon: string;
    earned_at: string;
  }>;
  onTempleClick: (temple: Temple) => void;
}

export function DashboardContent({
  user,
  temples,
  notifications,
  epicGoal,
  achievements,
  onTempleClick,
}: ContentProps) {
  const navigate = useNavigate();
  const featuredTemple = temples.find((t) => t.is_featured);
  const otherTemples = temples.filter((t) => !t.is_featured).slice(0, 6);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Bom dia";
    if (hour < 18) return "Boa tarde";
    return "Boa noite";
  };

  const completedTemples = temples.filter(
    (t) => t.status === "completed",
  ).length;

  const metrics = [
    {
      icon: Map,
      value: `${completedTemples}/7`,
      label: "Templos",
      color: "text-accent-800",
      bg: "bg-accent-800/[0.06]",
    },
    {
      icon: Target,
      value: `${Math.round(epicGoal.progress_percentage)}%`,
      label: "Meta",
      color: "text-emerald-500",
      bg: "bg-emerald-500/[0.06]",
    },
    {
      icon: Star,
      value: String(user.xp),
      label: "XP Total",
      color: "text-blue-500",
      bg: "bg-blue-500/[0.06]",
    },
    {
      icon: TrendingUp,
      value: `Nv ${user.level}`,
      label: "Level",
      color: "text-orange-500",
      bg: "bg-orange-500/[0.06]",
    },
  ];

  return (
    <div className="px-8 py-7 overflow-y-auto premium-scrollbar">
      <div className="max-w-6xl mx-auto">
        {/* Greeting */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-neutral-800 tracking-tight leading-tight">
            {getGreeting()},{" "}
            <span className="text-accent-800">
              {user.full_name.split(" ")[0]}
            </span>
          </h1>
          <p className="text-sm text-neutral-400 mt-1 font-medium">
            Sua jornada continua. Hoje e um otimo dia para construir o seu SIM.
          </p>
        </div>

        {/* 4 Metrics */}
        <div className="grid grid-cols-4 gap-3 mb-5">
          {metrics.map((m) => (
            <div
              key={m.label}
              className="bg-white rounded-xl border border-black/[0.06] shadow-[0_1px_3px_rgba(0,0,0,0.03)] p-4 text-center"
            >
              <div
                className={cn(
                  "w-8 h-8 rounded-lg flex items-center justify-center mx-auto mb-2",
                  m.bg,
                )}
              >
                <m.icon className={cn("w-4 h-4", m.color)} />
              </div>
              <p className="text-xl font-bold text-neutral-800 tabular-nums leading-tight">
                {m.value}
              </p>
              <p className="text-[9px] text-neutral-400 font-semibold uppercase tracking-[0.08em] mt-1">
                {m.label}
              </p>
            </div>
          ))}
        </div>

        {/* Current Temple — wide card */}
        {featuredTemple && (
          <div
            className="mb-5 bg-white rounded-xl border border-black/[0.06] shadow-[0_1px_3px_rgba(0,0,0,0.03)] overflow-hidden cursor-pointer group hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)] hover:border-accent-800/[0.12] transition-all"
            onClick={() => onTempleClick(featuredTemple)}
          >
            <div className="h-[2px] bg-gradient-to-r from-accent-800 via-accent-600 to-accent-800/20" />
            <div className="p-5">
              <div className="flex items-center gap-4">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 shadow-[0_2px_4px_rgba(0,0,0,0.1)]"
                  style={{ backgroundColor: featuredTemple.theme_color }}
                >
                  {React.createElement(getTempleIcon(featuredTemple.icon), {
                    className: "w-6 h-6 text-white",
                  })}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-[9px] font-bold text-accent-800 bg-accent-800/[0.07] px-1.5 py-[2px] rounded-[4px] uppercase tracking-wider">
                      Templo Atual
                    </span>
                  </div>
                  <h3 className="text-sm font-bold text-neutral-800 group-hover:text-accent-800 transition-colors">
                    {featuredTemple.name}
                  </h3>
                  <p className="text-xs text-neutral-400 mt-0.5">
                    {featuredTemple.total_tasks} tarefas &middot;{" "}
                    {Math.round(featuredTemple.progress_percentage || 0)}%
                    completo
                  </p>
                </div>
                <div className="text-right mr-2 flex-shrink-0">
                  <p className="text-2xl font-bold text-accent-800 tabular-nums">
                    {Math.round(featuredTemple.progress_percentage || 0)}%
                  </p>
                  <p className="text-[9px] text-neutral-400 font-semibold uppercase tracking-wider">
                    completo
                  </p>
                </div>
                <button
                  className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-accent-800 to-accent-600 text-white text-[13px] font-semibold rounded-xl shadow-[0_1px_3px_rgba(195,150,74,0.25)] hover:shadow-[0_2px_8px_rgba(195,150,74,0.3)] transition-all flex-shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/templo/${featuredTemple.id}/posicionamento`);
                  }}
                >
                  Continuar <ArrowRight className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-3 h-[4px] bg-black/[0.04] rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-accent-800 to-accent-600 rounded-full transition-all duration-700"
                  style={{
                    width: `${featuredTemple.progress_percentage || 0}%`,
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* First Steps */}
        <div className="mb-5">
          <FirstStepsChecklist />
        </div>

        {/* Epic Goal + Achievements — 2 col */}
        <div className="grid grid-cols-2 gap-3 mb-5">
          {/* Meta Epica */}
          <div className="bg-white rounded-xl border border-black/[0.06] shadow-[0_1px_3px_rgba(0,0,0,0.03)] overflow-hidden">
            <div className="h-[2px] bg-gradient-to-r from-accent-800 via-accent-600 to-accent-800/20" />
            <div className="p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] font-bold text-accent-800 bg-accent-800/[0.07] px-2 py-[3px] rounded-[5px] uppercase tracking-[0.06em]">
                  Meta Epica
                </span>
                <span className="text-xs text-neutral-400 flex items-center gap-1 font-medium">
                  <Clock className="w-3 h-3" /> {epicGoal.deadline_weeks} sem
                </span>
              </div>
              <h2 className="text-base font-bold text-neutral-800 leading-snug tracking-tight">
                {epicGoal.title}
              </h2>
              <p className="text-xs text-neutral-400 mt-1">
                Status:{" "}
                <span className="text-accent-800 font-semibold">
                  {epicGoal.status}
                </span>
              </p>
              <div className="mt-3 space-y-1.5">
                <div className="flex justify-between text-xs">
                  <span className="text-neutral-400 font-medium">
                    Progresso
                  </span>
                  <span className="text-neutral-800 font-bold tabular-nums">
                    {Math.round(epicGoal.progress_percentage)}%
                  </span>
                </div>
                <div className="h-[5px] w-full bg-black/[0.04] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-accent-800 to-accent-600 rounded-full transition-all duration-1000 ease-out"
                    style={{ width: `${epicGoal.progress_percentage}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Conquistas */}
          <div className="bg-white rounded-xl border border-black/[0.06] shadow-[0_1px_3px_rgba(0,0,0,0.03)]">
            <div className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-accent-800" />
                  <span className="text-sm font-bold text-neutral-800">
                    Conquistas
                  </span>
                </div>
                <button
                  className="text-xs text-accent-800/80 font-semibold hover:text-accent-800 transition-colors"
                  onClick={() => navigate("/notificacoes")}
                >
                  Ver todas
                </button>
              </div>
              {achievements.length > 0 ? (
                <div className="space-y-2">
                  {achievements.slice(0, 3).map((achievement, index) => {
                    const Icon = getAchievementIcon(achievement.icon);
                    return (
                      <div
                        key={index}
                        className="flex items-center gap-3 p-2.5 rounded-[10px] bg-black/[0.015] border border-black/[0.04]"
                      >
                        <div className="w-8 h-8 bg-accent-800/[0.06] rounded-[8px] flex items-center justify-center flex-shrink-0 ring-1 ring-accent-800/[0.06]">
                          <Icon className="w-4 h-4 text-accent-800" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-neutral-700 truncate">
                            {achievement.name}
                          </p>
                          <p className="text-[10px] text-neutral-400 truncate">
                            {achievement.description}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-6 text-center">
                  <div className="w-10 h-10 bg-accent-800/[0.06] rounded-xl flex items-center justify-center mb-2">
                    <Trophy className="w-5 h-5 text-accent-800/40" />
                  </div>
                  <p className="text-xs text-neutral-400">
                    Complete tarefas para ganhar conquistas
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          {[
            {
              icon: Map,
              label: "Continuar Jornada",
              desc: "Mapa dos Templos",
              path: "/jornada",
            },
            {
              icon: Users,
              label: "Falar com Mentor",
              desc: "Suporte 24/7",
              path: "/mentor",
            },
            {
              icon: Building2,
              label: "Meu Negocio",
              desc: "Visao estrategica",
              path: "/meu-negocio",
            },
          ].map((action) => (
            <button
              key={action.path}
              onClick={() => navigate(action.path)}
              className="bg-white rounded-xl border border-black/[0.06] shadow-[0_1px_3px_rgba(0,0,0,0.03)] p-4 text-left hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)] hover:border-accent-800/[0.12] transition-all group flex items-center gap-3"
            >
              <div className="w-9 h-9 rounded-[9px] bg-accent-800/[0.06] border border-accent-800/[0.08] flex items-center justify-center flex-shrink-0">
                <action.icon className="w-4 h-4 text-accent-800" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-neutral-700 group-hover:text-accent-800 transition-colors">
                  {action.label}
                </p>
                <p className="text-[10px] text-neutral-400">{action.desc}</p>
              </div>
              <ArrowRight className="w-3.5 h-3.5 text-neutral-300 group-hover:text-accent-800 transition-colors flex-shrink-0" />
            </button>
          ))}
        </div>

        {/* Reminders */}
        {notifications.recent_reminders.length > 0 && (
          <div className="mb-5 rounded-xl bg-white border border-black/[0.06] shadow-[0_1px_3px_rgba(0,0,0,0.03)] overflow-hidden">
            <div className="flex">
              <div className="w-[3px] bg-accent-800 flex-shrink-0" />
              <div className="flex items-start gap-3.5 p-4 flex-1">
                <div className="w-8 h-8 bg-accent-800/[0.06] rounded-[9px] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Bell className="w-[14px] h-[14px] text-accent-800" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-2.5">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-neutral-800">
                        Lembretes
                      </span>
                      <span className="text-[10px] font-bold text-accent-800 bg-accent-800/[0.07] px-1.5 py-[2px] rounded-[4px] tabular-nums">
                        {notifications.recent_reminders.length}
                      </span>
                    </div>
                    <button
                      className="text-xs text-accent-800/80 font-semibold hover:text-accent-800 flex items-center gap-0.5 transition-colors"
                      onClick={() => navigate("/notificacoes")}
                    >
                      Ver todos <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="space-y-1.5">
                    {notifications.recent_reminders.map((reminder, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <div className="w-[5px] h-[5px] bg-accent-800/40 rounded-full flex-shrink-0 mt-[7px]" />
                        <span className="text-[13px] text-neutral-500 leading-[1.6]">
                          {reminder.message}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Temples */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Map className="w-4 h-4 text-accent-800" />
              <h2 className="text-base font-bold text-neutral-800 tracking-tight">
                Templos de Poder
              </h2>
            </div>
            <button
              className="text-xs text-accent-800/80 font-semibold hover:text-accent-800 transition-colors"
              onClick={() => navigate("/jornada")}
            >
              Ver Mapa
            </button>
          </div>

          <div className="grid grid-cols-3 gap-3">
            {otherTemples.map((temple) => {
              const Icon = getTempleIcon(temple.icon);
              const isLocked = temple.status === "locked";

              return (
                <div
                  key={temple.id}
                  className={cn(
                    "rounded-xl border p-3.5 transition-all duration-200 group",
                    isLocked
                      ? "bg-neutral-50/50 border-black/[0.04] opacity-60 cursor-default"
                      : "bg-white border-black/[0.06] shadow-[0_1px_3px_rgba(0,0,0,0.03)] cursor-pointer hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)] hover:border-accent-800/[0.12] hover:-translate-y-[1px]",
                  )}
                  onClick={() => !isLocked && onTempleClick(temple)}
                >
                  <div className="flex items-start justify-between mb-2.5">
                    <div
                      className={cn(
                        "w-9 h-9 rounded-[9px] flex items-center justify-center shadow-[0_1px_3px_rgba(0,0,0,0.1)]",
                        isLocked && "grayscale opacity-50 shadow-none",
                      )}
                      style={{ backgroundColor: temple.theme_color }}
                    >
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    {isLocked && <Lock className="w-3 h-3 text-neutral-300" />}
                  </div>

                  <h3
                    className={cn(
                      "text-sm font-semibold leading-tight mb-0.5",
                      isLocked ? "text-neutral-400" : "text-neutral-800",
                    )}
                  >
                    {temple.name}
                  </h3>

                  <p className="text-xs text-neutral-400 line-clamp-2 leading-relaxed mb-3">
                    {temple.description}
                  </p>

                  <div className="flex items-center justify-between">
                    <span
                      className={cn(
                        "text-[10px] font-bold tabular-nums",
                        isLocked ? "text-neutral-300" : "text-accent-800",
                      )}
                    >
                      {isLocked
                        ? "Bloqueado"
                        : `${Math.round(temple.progress_percentage || 0)}%`}
                    </span>
                    {!isLocked && (
                      <div className="w-10 h-[3px] bg-black/[0.04] rounded-full overflow-hidden">
                        <div
                          className="h-full bg-accent-800 rounded-full"
                          style={{
                            width: `${temple.progress_percentage || 0}%`,
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
